import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AIStarIcon, CheckIcon, ChevronDownIcon, ArrowRightIcon, CodeIcon, BrainIcon, HardhatIcon, AgentIcon, ShieldIcon, DataIcon } from '../components/Icons';
import { ContactSection } from '../components/Layout';

function Home() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const solutions = [
    {
      icon: <AgentIcon />,
      title: 'Custom AI Agent Deployments',
      description: 'Your own AI agent, deployed on private infrastructure. Text it on Telegram — it builds tools, organizes data, and automates workflows. For anyone, any profession.',
      link: '/agents',
      linkText: 'Learn more'
    },
    {
      icon: <HardhatIcon />,
      title: 'AI Construction Synthesis',
      description: 'Purpose-built AI for heavy industrial construction. Estimating, project data, reporting — built by someone who spent 25+ years on the job.',
      link: '/construction',
      linkText: 'Learn more'
    },
    {
      icon: <CodeIcon />,
      title: 'Custom Software Development',
      description: 'Full-stack applications powered by AI. From internal tools to customer-facing products — designed around how you actually work, not how software thinks you should.'
    },
    {
      icon: <DataIcon />,
      title: 'Data Architecture & Integration',
      description: 'Transform spreadsheet chaos into structured systems your AI can query in plain English. Connect your CRM, project management, accounting — whatever you run on.'
    },
    {
      icon: <BrainIcon />,
      title: 'AI Strategy & Implementation',
      description: 'Not a slide deck. A working plan. We assess your operations, identify where AI creates real value, and build it. You get working software, not a report.'
    },
    {
      icon: <ShieldIcon />,
      title: 'Private Infrastructure',
      description: 'Everything runs on infrastructure you own. No SaaS hostage situations. No shared servers. Zero-trust networking via Tailscale. Your data stays yours.'
    },
  ];

  const heroBenefits = [
    'AI agents that run 24/7 on your own infrastructure',
    'Custom software built around your actual workflows',
    'Industry-specific AI that learns your domain',
  ];

  const differentiators = [
    {
      title: 'Built by a builder.',
      description: 'Our founder spent 25+ years in heavy industrial construction before teaching himself to code and deploy AI. We understand real operations — not just technology.'
    },
    {
      title: 'Software that works for you.',
      description: 'We don\'t sell seats or subscriptions. We build custom solutions on infrastructure you control. When we\'re done, you own everything — the code, the data, the agents.'
    },
    {
      title: 'AI that actually does work.',
      description: 'Not chatbots. Not dashboards. Persistent AI agents that estimate jobs, build tools, manage data, and automate workflows. The kind of AI that replaces "I wish someone would..."'
    }
  ];

  return (
    <>
      <section className="hero">
        <div className="hero-background">
          <div className="hero-grid"></div>
          <div className="hero-background-star" style={{ transform: `translate(-50%, -50%) translateY(${scrollY * 0.3}px)` }}>
            <AIStarIcon />
          </div>
          <div className="hero-gradient"></div>
        </div>
        <div className="container">
          <div className="hero-inner">
            <div className="hero-copy">
              <p className="hero-eyebrow">AI software that does real work</p>
              <h1 className="hero-title">
                Custom AI Software Solutions
              </h1>
              <p className="hero-subtitle">
                We build AI-powered software, deploy personal agents, and create tools that transform how you operate — all on private infrastructure you own.
              </p>
              <div className="hero-buttons">
                <button
                  className="btn btn-primary"
                  onClick={() => scrollToSection('contact')}
                >
                  Start a Project
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => scrollToSection('solutions')}
                >
                  See What We Build
                </button>
              </div>
            </div>
            <div className="hero-side">
              <div className="hero-side-card">
                <h3>What we bring to the table</h3>
                <ul className="hero-benefits">
                  {heroBenefits.map((benefit, index) => (
                    <li key={index} className="hero-benefit">
                      <span className="hero-benefit-icon"><CheckIcon /></span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
                <div className="hero-side-footer">Private infrastructure • You own everything</div>
              </div>
            </div>
          </div>
        </div>
        <div className="scroll-indicator" onClick={() => scrollToSection('solutions')}>
          <ChevronDownIcon />
        </div>
      </section>

      {/* Solutions Grid */}
      <section id="solutions" className="services">
        <div className="container">
          <div className="section-header">
            <h2>What we build</h2>
            <p>AI-powered software and agent deployments for businesses that need more than a chatbot.</p>
          </div>
          <div className="services-grid">
            {solutions.map((solution, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {solution.icon}
                </div>
                <h3>{solution.title}</h3>
                <p>{solution.description}</p>
                {solution.link && (
                  <Link to={solution.link} className="card-link">
                    {solution.linkText} <ArrowRightIcon />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About / Differentiators */}
      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>Not another AI consultancy.</h2>
              <p>I spent 25 years in heavy industrial construction — tradesman, foreman, estimator, project manager. I watched good crews lose money on bad data. Projects bleed because reporting was weeks behind reality.</p>
              <p>In 2023, I stopped waiting for someone else to fix it. I taught myself to code, then AI, then infrastructure. I deployed my own agent — Ezekiel — on a VPS, connected it to my messaging, my estimating software, my project data.</p>
              <p>Now it estimates jobs from email. Builds tools I need on the fly. Manages my data, designs my materials, runs my operations. It's not an assistant I talk to sometimes — it's infrastructure I can't work without.</p>
              <p><strong>That's what we build for you.</strong></p>
              <p className="founder-sig">- Keaton Valentine, Founder</p>
            </div>
            <div className="about-visual">
              <div className="about-highlight">
                <h3>Why whatifWE</h3>
                <div className="about-differentiators">
                  {differentiators.map((item, index) => (
                    <div key={index} className="differentiator">
                      <h4>{item.title}</h4>
                      <p>{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Solutions Cards */}
      <section className="featured-solutions">
        <div className="container">
          <div className="section-header">
            <h2>Explore our specialties</h2>
            <p>Deep expertise in construction AI and personal agent deployments.</p>
          </div>
          <div className="featured-grid">
            <Link to="/construction" className="featured-card featured-construction">
              <div className="featured-card-content">
                <div className="featured-icon"><HardhatIcon /></div>
                <h3>AI Construction Synthesis</h3>
                <p>Purpose-built AI for heavy industrial construction. Estimating, project data architecture, automated reporting. Built by a construction veteran.</p>
                <span className="featured-link">Explore Construction AI <ArrowRightIcon /></span>
              </div>
            </Link>
            <Link to="/agents" className="featured-card featured-agents">
              <div className="featured-card-content">
                <div className="featured-icon"><AgentIcon /></div>
                <h3>Custom AI Agent Deployments</h3>
                <p>Text your agent on Telegram. It builds custom tools, organizes your life, and automates your workflows — no coding needed. For anyone with a workflow to simplify.</p>
                <span className="featured-link">Explore Agent Deployments <ArrowRightIcon /></span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="testimonial">
        <div className="container">
          <div className="testimonial-card">
            <p className="testimonial-quote">
              "My agent estimated a construction job from two pasted emails this morning. Then it designed a print banner for my company. Then it reviewed my landing page source code. Before lunch. It's not a tool I use — it's a coworker that never clocks out."
            </p>
            <div className="testimonial-author">
              <span className="author-name">Keaton Valentine</span>
              <span className="author-company">whatifWE LLC — Running his own agent since 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to build something real?</h2>
            <p>Tell us what you're working on. We'll show you what's possible.</p>
            <ul className="cta-points">
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>Custom AI software tailored to your operations</span></li>
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>Private infrastructure — no shared servers, no data leaks</span></li>
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>You own everything we build</span></li>
            </ul>
            <button className="btn btn-primary" onClick={() => scrollToSection('contact')}>
              Let's Talk
            </button>
          </div>
        </div>
      </section>

      <ContactSection />
    </>
  );
}

export default Home;
